import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { socket } from '../socket';
import type { ClientInfo, Moment, QueueMessage } from '../types';
import { loadSession, saveSession, clearSession } from '../utils/session';
import { setIdentityName, getOrCreateIdentity } from '../utils/identity';
import { getStoredToken, getAuthPayload } from '../utils/auth';
import { trackEvent } from '../analytics/posthog';
import { isDebugEnabled } from '../utils/debug';
import { buildDisplayQueue } from '../utils/queue';

function dedupeClientsByUserId(clients: ClientInfo[]): ClientInfo[] {
  const seen = new Set<string>();
  return (clients || []).filter((c) => {
    if (!c?.userId || seen.has(c.userId)) return false;
    seen.add(c.userId);
    return true;
  });
}

function dedupeById<T extends { id?: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  return (items || []).filter((item) => {
    if (!item?.id || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  }) as T[];
}

export function useSocketRoom(
  roomId: string | undefined,
  identity: { id: string; name: string; avatarUrl?: string },
  displayName: string
) {
  const [queue, setQueue] = useState<QueueMessage[]>([]);
  const [isHost, setIsHost] = useState(false);
  const [isQueueOpen, setIsQueueOpen] = useState(true);
  const [maxMessagesPerUser, setMaxMessagesPerUser] = useState(5);
  const [countdownSeconds, setCountdownSeconds] = useState(30);
  const [isProtected, setIsProtected] = useState(false);
  const [isStageProtected, setIsStageProtected] = useState(false);
  const [hideAvatars, setHideAvatars] = useState(false);
  type JoinStatus = 'idle' | 'joining' | 'joined' | 'ready' | 'error';
  const [joinStatus, setJoinStatus] = useState<JoinStatus>('idle');
  const [joinError, setJoinError] = useState<string | null>(null);
  const [showUpNextBanner, setShowUpNextBanner] = useState(false);
  const [roomEnded, setRoomEnded] = useState(false);
  const [nowPlayingId, setNowPlayingId] = useState<string | null>(null);
  const [activeMoment, setActiveMoment] = useState<Moment | null>(null);
  const [activeMomentEnd, setActiveMomentEnd] = useState<string | null>(null);
  const [momentTemplates, setMomentTemplates] = useState<Moment[]>([]);
  const [queueMoments, setQueueMoments] = useState<Moment[]>([]);
  const [showFullQueue, setShowFullQueue] = useState(false);
  // Palco: stageLive é o "sinal do host" — a fila só anda depois do
  // bigpicture_go_live. Default true (comportamento antigo para gateways sem o
  // campo); o primeiro state de um gateway novo corrige para false em salas
  // que ainda não receberam o sinal. As restantes flags vêm no state para os
  // host controls refletirem a realidade após reloads (antes só chegavam por
  // broadcast).
  const [stageLive, setStageLive] = useState(true);
  const [bpQRShown, setBpQRShown] = useState(false);
  const [bpLeaderboardShown, setBpLeaderboardShown] = useState(false);
  const [bpCountdownPaused, setBpCountdownPaused] = useState(false);
  const [bpVideoPaused, setBpVideoPaused] = useState(false);

  const displayQueue = useMemo(() => buildDisplayQueue(queue, queueMoments), [queue, queueMoments]);
  const [clients, setClients] = useState<ClientInfo[]>([]);
  const [blockedUsers, setBlockedUsers] = useState<string[]>([]);
  const [bigPictureViewers, setBigPictureViewers] = useState(0);
  const [roomJoinedReceived, setRoomJoinedReceived] = useState(false);

  const hasAttemptedAutoJoin = useRef(false);
  const attemptedAutoJoinForRoom = useRef<string | null>(null);
  const lastNotifiedTopIdRef = useRef<string | null>(null);
  const lastSeqRef = useRef<number>(-1);
  const joinStatusRef = useRef<JoinStatus>(joinStatus);
  joinStatusRef.current = joinStatus;
  const roomJoinedReceivedRef = useRef(false);
  const stateReceivedRef = useRef(false);

  const doJoin = useCallback(
    (name: string, pwd?: string) => {
      if (!roomId) return;
      if (isDebugEnabled('room') || isDebugEnabled('all')) {
        console.log('[useSocketRoom] doJoin called', { roomId, name });
      }
      setJoinError(null);
      setJoinStatus('joining');
      const latestIdentity = getOrCreateIdentity();
      const auth = getAuthPayload();
      const finalName = name.trim() || latestIdentity.name || auth?.userName || 'Guest';
      const finalUserId = auth?.userId || latestIdentity.id;
      const finalAvatar = latestIdentity.avatarUrl || auth?.avatarUrl || undefined;
      setIdentityName(finalName);
      if (finalUserId !== latestIdentity.id || finalAvatar) {
        // persist merged identity so later addVideo uses the right values
        getOrCreateIdentity();
        if (finalUserId) localStorage.setItem('karaokr_event_user_id', finalUserId);
        if (finalAvatar) localStorage.setItem('karaokr_event_user_avatar', finalAvatar);
      }
      const sesh = loadSession();
      saveSession({ roomId, displayName: finalName, password: pwd, maxMessages: sesh?.maxMessages ?? 5 });
      socket.connect();
      const token = getStoredToken();
      if (token) socket.emit('authenticate', { token });
      socket.emit('join', {
        roomId,
        userId: finalUserId,
        userName: finalName,
        avatarUrl: finalAvatar,
        maxMessages: sesh?.maxMessages ?? 5,
        password: pwd,
      });
    },
    [roomId]
  );

  useEffect(() => {
    if (!roomId) return;
    // Reset join status until the room actor confirms the join with a fresh state.
    if (joinStatus !== 'idle') {
      setJoinStatus('idle');
    }
    setJoinError(null);
    lastSeqRef.current = -1;
    roomJoinedReceivedRef.current = false;
    stateReceivedRef.current = false;
    setRoomJoinedReceived(false);
    // Set the socket room context before registering listeners so stale cached
    // events from a previous room are never delivered to this room's handlers.
    socket.setRoomContext(roomId);
    socket.connect();

    const onConnect = () => {
      const token = getStoredToken();
      if (token) socket.emit('authenticate', { token });
    };
    socket.on('connect', onConnect);
    if (socket.connected) onConnect();

    const applyState = (state: any) => {
      if (!state || state.roomId !== roomId) return;
      const seq = typeof state.seq === 'number' ? state.seq : -1;
      if (seq >= 0 && seq < lastSeqRef.current) {
        if (isDebugEnabled('room') || isDebugEnabled('all')) {
          console.log('[useSocketRoom] ignoring stale state', { seq, last: lastSeqRef.current });
        }
        return;
      }
      if (seq >= 0) lastSeqRef.current = seq;
      if (Array.isArray(state.queue)) {
        setQueue(dedupeById(state.queue));
        setIsQueueOpen(state.isQueueOpen ?? true);
        setMaxMessagesPerUser(state.maxMessages ?? 5);
        setCountdownSeconds(state.countdownSeconds ?? 30);
        setNowPlayingId(state.nowPlaying?.id ?? null);
      }
      if (Array.isArray(state.clients)) {
        setClients(dedupeClientsByUserId(state.clients));
      }
      if (Array.isArray(state.blockedUsers)) {
        setBlockedUsers(state.blockedUsers);
      }
      setIsStageProtected(state.isStageProtected ?? false);
      setHideAvatars(state.hideAvatars ?? false);
      // showFullQueue também vem nos snapshots de estado — sem isto, um cliente
      // que perdesse o broadcast show_full_queue_update (ex.: join a correr
      // paralelo ao toggle do host) nunca via a fila partilhada.
      if (typeof state.showFullQueue === 'boolean') {
        setShowFullQueue(state.showFullQueue);
      }
      // Estado do palco. Gateways sem o campo (deploy a meio) mantêm o
      // comportamento antigo de arranque automático — só um stageLive
      // explícito false (sala nova no gateway novo) fica em espera.
      if (typeof state.stageLive === 'boolean') {
        setStageLive(state.stageLive);
      } else {
        setStageLive(true);
      }
      if (typeof state.qrShown === 'boolean') {
        setBpQRShown(state.qrShown);
      }
      if (typeof state.leaderboardShown === 'boolean') {
        setBpLeaderboardShown(state.leaderboardShown);
      }
      if (typeof state.countdownPaused === 'boolean') {
        setBpCountdownPaused(state.countdownPaused);
      }
      if (typeof state.videoPaused === 'boolean') {
        setBpVideoPaused(state.videoPaused);
      }
      if (Array.isArray(state.moments)) {
        setQueueMoments(dedupeById(state.moments));
      }
      if (state.activeMoment) {
        setActiveMoment(state.activeMoment);
        setActiveMomentEnd(state.activeMomentEnd ?? null);
      } else if (state.activeMoment === null) {
        setActiveMoment(null);
        setActiveMomentEnd(null);
      }
      // A state snapshot confirms the room actor knows us, but we only declare
      // the local session ready once the server has also sent room_joined so
      // that isHost/isProtected are authoritative before consumers act on them.
      stateReceivedRef.current = true;
      if (joinStatus !== 'ready' && roomJoinedReceivedRef.current) {
        setJoinStatus('ready');
      }
    };

    const onState = (state: any) => {
      if (isDebugEnabled('room') || isDebugEnabled('all')) {
        console.log('[useSocketRoom] state received', state);
      }
      applyState(state);
    };

    const onQueueUpdate = (payload: QueueMessage[] | { queue?: QueueMessage[]; seq?: number }) => {
      const incomingQueue = Array.isArray(payload) ? payload : payload?.queue;
      const seq = Array.isArray(payload) ? -1 : payload?.seq ?? -1;
      if (seq >= 0 && seq < lastSeqRef.current) {
        if (isDebugEnabled('room') || isDebugEnabled('all')) {
          console.log('[useSocketRoom] ignoring stale queue_update', { seq, last: lastSeqRef.current });
        }
        return;
      }
      if (seq >= 0) lastSeqRef.current = seq;
      // Defensive deduplication: never render duplicate message ids.
      const deduped = dedupeById(incomingQueue || []);
      if (isDebugEnabled('room') || isDebugEnabled('all')) {
        console.log('[useSocketRoom] queue_update applied', { seq, len: deduped.length });
      }
      setQueue(deduped);

      const top = deduped[0];
      if (top && top.userId === identity.id && top.id !== lastNotifiedTopIdRef.current) {
        lastNotifiedTopIdRef.current = top.id;
        setShowUpNextBanner(true);
        playBeepSound();
        if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
          try {
            new Notification("You're up next! 🎤", {
              body: top.content,
              icon: '/vite.svg',
            });
          } catch {
            // ignore
          }
        }
        const t = setTimeout(() => setShowUpNextBanner(false), 5000);
        return () => clearTimeout(t);
      }
    };

    const onNowPlayingUpdate = (data: { nowPlaying?: QueueMessage | null; messageId?: string | null; seq?: number }) => {
      // Guarda de seq igual à de queue_update/state: uma cópia stale deste
      // evento (seq inferior ao que já aplicámos) nunca deve sobrescrever o
      // nowPlaying atual. O seq vem dentro do payload (gateway >= out/2026).
      const seq = typeof data?.seq === 'number' ? data.seq : -1;
      if (seq >= 0 && seq < lastSeqRef.current) {
        if (isDebugEnabled('room') || isDebugEnabled('all')) {
          console.log('[useSocketRoom] ignoring stale now_playing_update', { seq, last: lastSeqRef.current });
        }
        return;
      }
      if (seq >= 0) lastSeqRef.current = seq;
      setNowPlayingId(data.nowPlaying?.id ?? data.messageId ?? null);
    };

    const onMomentStarted = (moment: Moment) => {
      setActiveMoment(moment);
      const duration = moment.duration && moment.duration > 0 ? moment.duration : moment.type === 'intro' ? 10 : 300;
      setActiveMomentEnd(new Date(Date.now() + duration * 1000).toISOString());
    };

    const onMomentEnded = () => {
      setActiveMoment(null);
      setActiveMomentEnd(null);
    };

    const onMomentsList = (data: { moments: Moment[] }) => {
      setMomentTemplates(data.moments ?? []);
    };

    const onMomentsUpdate = (data: { moments?: Moment[] }) => {
      if (Array.isArray(data.moments)) {
        setQueueMoments(data.moments);
      }
    };

    const onShowFullQueueUpdate = (data: { showFullQueue: boolean }) => {
      setShowFullQueue(data.showFullQueue ?? false);
    };
    const onBlockedUsersUpdate = (data: { blockedUsers?: string[] }) => {
      if (isDebugEnabled('room') || isDebugEnabled('all')) {
        console.log('[useSocketRoom] blocked_users_update', data.blockedUsers);
      }
      setBlockedUsers(data.blockedUsers ?? []);
    };

    const onBigPictureViewersUpdate = (data: { count?: number }) => {
      if (isDebugEnabled('room') || isDebugEnabled('all')) {
        console.log('[useSocketRoom] bigpicture_viewers_update', data.count);
      }
      setBigPictureViewers(data.count ?? 0);
    };

    const onRoomJoined = (data: any) => {
      if (isDebugEnabled('room') || isDebugEnabled('all')) {
        console.log('[useSocketRoom] room_joined received', data);
      }
      setIsHost(data.isHost ?? data.isAdmin ?? false);
      setIsQueueOpen(data.isQueueOpen ?? true);
      setIsProtected(data.isProtected ?? false);
      setIsStageProtected(data.isStageProtected ?? false);
      setMaxMessagesPerUser(data.maxMessages ?? data.maxMessagesPerUser ?? 5);
      setCountdownSeconds(data.countdownSeconds ?? 30);
      // joinStatus becomes 'joined' here and 'ready' once a state snapshot has
      // also arrived. This prevents consumers from acting on isHost before both
      // the join confirmation and the full room state are authoritative.
      roomJoinedReceivedRef.current = true;
      setRoomJoinedReceived(true);
      setJoinStatus((prev) => {
        if (prev === 'ready') return 'ready';
        return stateReceivedRef.current ? 'ready' : 'joined';
      });
      setJoinError(null);
      if (roomId) {
        const sesh = loadSession();
        if (!sesh || sesh.roomId !== roomId) {
          saveSession({ roomId, displayName });
        }
      }
    };

    const onQueueStatus = (open: boolean) => setIsQueueOpen(open);
    const onMaxMessagesUpdate = (max: number) => setMaxMessagesPerUser(max);
    const onCountdownUpdate = (seconds: number) => setCountdownSeconds(seconds);

    const onRoomSettingsUpdated = (data: any) => {
      if (data.maxMessages !== undefined) {
        setMaxMessagesPerUser(data.maxMessages);
        // Keep the local session in sync so refreshes/rejoins use the latest limit.
        const sesh = loadSession();
        if (sesh) saveSession({ ...sesh, maxMessages: data.maxMessages });
      }
      if (data.countdownSeconds !== undefined) setCountdownSeconds(data.countdownSeconds);
      if (data.isProtected !== undefined) setIsProtected(data.isProtected);
      if (data.isStageProtected !== undefined) setIsStageProtected(data.isStageProtected);
      if (data.hideAvatars !== undefined) setHideAvatars(data.hideAvatars);
    };

    const onRoomEnded = (_data: any) => {
      setRoomEnded(true);
      setJoinStatus('idle');
      setJoinError(null);
      clearSession();
      socket.emit('leave', { roomId });
    };

    const onError = (msg: string) => {
      // Defensively ignore stale duplicate-name errors from old cached clients
      if (msg && msg.includes('already taken in this room')) {
        return;
      }
      // Only treat errors as join failures while we are still joining. Once the
      // room is ready, errors are transient and should not boot the user back to
      // the join gate.
      const isJoiningPhase = joinStatusRef.current === 'joining' || joinStatusRef.current === 'joined';
      if (!isJoiningPhase) {
        if (isDebugEnabled('room') || isDebugEnabled('all')) {
          console.log('[useSocketRoom] ignoring transient error after join', msg);
        }
        return;
      }
      if (msg === 'Incorrect room password.') {
        setJoinStatus('error');
        setJoinError('Wrong password. Please try again.');
        // Keep the session so the user can re-enter the password on the join
        // gate; clearing it here would force a full re-join every refresh.
      } else if (msg.includes("Room not found. This room doesn't exist yet")) {
        setJoinStatus('error');
        setJoinError(msg);
        clearSession();
      } else {
        setJoinStatus('error');
        setJoinError(msg);
        setTimeout(() => setJoinError((curr) => (curr === msg ? null : curr)), 3500);
      }
    };

    socket.on('queue_update', onQueueUpdate);
    socket.on('now_playing_update', onNowPlayingUpdate);
    socket.on('moment_started', onMomentStarted);
    socket.on('moment_ended', onMomentEnded);
    socket.on('moments_list', onMomentsList);
    socket.on('moments_update', onMomentsUpdate);
    socket.on('show_full_queue_update', onShowFullQueueUpdate);
    socket.on('blocked_users_update', onBlockedUsersUpdate);
    socket.on('bigpicture_viewers_update', onBigPictureViewersUpdate);
    socket.on('state', onState);
    socket.on('room_joined', onRoomJoined);
    socket.on('queue_status_update', onQueueStatus);
    socket.on('max_messages_update', onMaxMessagesUpdate);
    socket.on('countdown_update', onCountdownUpdate);
    socket.on('update_room_settings', onRoomSettingsUpdated);
    socket.on('room_ended', onRoomEnded);
    socket.on('queue_error', onError);

    if (attemptedAutoJoinForRoom.current !== roomId) {
      attemptedAutoJoinForRoom.current = roomId;
      hasAttemptedAutoJoin.current = false;
    }
    if (!hasAttemptedAutoJoin.current) {
      hasAttemptedAutoJoin.current = true;
      const sesh = loadSession();
      if (sesh && sesh.roomId === roomId) {
        const t = setTimeout(() => doJoin(sesh.displayName, sesh.password), 300);
        return () => {
          clearTimeout(t);
          socket.off('queue_update', onQueueUpdate);
          socket.off('now_playing_update', onNowPlayingUpdate);
          socket.off('moment_started', onMomentStarted);
          socket.off('moment_ended', onMomentEnded);
          socket.off('moments_list', onMomentsList);
          socket.off('moments_update', onMomentsUpdate);
          socket.off('show_full_queue_update', onShowFullQueueUpdate);
          socket.off('state', onState);
          socket.off('room_joined', onRoomJoined);
          socket.off('queue_status_update', onQueueStatus);
          socket.off('max_messages_update', onMaxMessagesUpdate);
          socket.off('countdown_update', onCountdownUpdate);
          socket.off('room_ended', onRoomEnded);
          socket.off('queue_error', onError);
          socket.emit('leave', { roomId });
        };
      }
    }

    return () => {
      socket.off('connect', onConnect);
      socket.off('queue_update', onQueueUpdate);
      socket.off('now_playing_update', onNowPlayingUpdate);
      socket.off('moment_started', onMomentStarted);
      socket.off('moment_ended', onMomentEnded);
      socket.off('moments_list', onMomentsList);
      socket.off('moments_update', onMomentsUpdate);
      socket.off('show_full_queue_update', onShowFullQueueUpdate);
      socket.off('blocked_users_update', onBlockedUsersUpdate);
      socket.off('bigpicture_viewers_update', onBigPictureViewersUpdate);
      socket.off('state', onState);
      socket.off('room_joined', onRoomJoined);
      socket.off('queue_status_update', onQueueStatus);
      socket.off('max_messages_update', onMaxMessagesUpdate);
      socket.off('countdown_update', onCountdownUpdate);
      socket.off('update_room_settings', onRoomSettingsUpdated);
      socket.off('room_ended', onRoomEnded);
      socket.off('queue_error', onError);
      socket.emit('leave', { roomId });
    };
  }, [roomId]);

  const popTop = useCallback(() => socket.emit('delete_top_message', { roomId }), [roomId]);
  const setNowPlaying = useCallback((messageId: string) => socket.emit('set_now_playing', { roomId, messageId }), [roomId]);
  const startMoment = useCallback((moment: Moment) => socket.emit('start_moment', { roomId, moment }), [roomId]);
  const endMoment = useCallback(() => socket.emit('end_moment', { roomId }), [roomId]);
  const listMoments = useCallback(() => socket.emit('list_moments', { roomId }), [roomId]);
  const addMoment = useCallback((moment: Moment) => {
    socket.emit('add_moment', { roomId, moment });
    trackEvent('moment_added', { roomId, title: moment.title });
  }, [roomId]);
  const removeMoment = useCallback((momentId: string) => socket.emit('remove_moment', { roomId, id: momentId }), [roomId]);
  const reorderMoments = useCallback(
    (orderedIds: string[], positions?: number[]) =>
      socket.emit('reorder_moments', { roomId, orderedIds, positions }),
    [roomId]
  );
  const updateShowFullQueue = useCallback((value: boolean) => socket.emit('update_show_full_queue', { roomId, value }), [roomId]);
  const goLive = useCallback(() => socket.emit('bigpicture_go_live', { roomId }), [roomId]);
  const stepAside = useCallback(() => socket.emit('step_aside', { roomId }), [roomId]);
  const swapNowPlaying = useCallback(
    (nowPlayingId?: string) => socket.emit('swap_now_playing', { roomId, nowPlayingId }),
    [roomId]
  );
  const toggleQueue = useCallback(() => socket.emit('toggle_queue_status', { roomId, isOpen: !isQueueOpen }), [roomId, isQueueOpen]);
  const updateMaxMessages = useCallback((max: number) => socket.emit('update_max_messages', { roomId, value: max }), [roomId]);
  const updateCountdown = useCallback((seconds: number) => socket.emit('update_countdown', { roomId, value: seconds }), [roomId]);
  const reorder = useCallback((newOrderIds: string[]) => socket.emit('reorder_queue', { roomId, newOrderIds }), [roomId]);
  const reorderOwnSongs = useCallback((orderedIds: string[]) => socket.emit('reorder_own_songs', { roomId, orderedIds }), [roomId]);
  const leave = useCallback(() => {
    socket.emit('leave', { roomId });
    clearSession();
  }, [roomId]);
  const endRoom = useCallback(() => {
    if (!roomId) return;
    socket.emit('end_room', { roomId });
  }, [roomId]);
  const updatePassword = useCallback((password: string) => socket.emit('update_room_password', { roomId, password }), [roomId]);
  const updateRoomSettings = useCallback(
    (settings: { maxMessages?: number; countdownSeconds?: number; password?: string; stagePassword?: string; hideAvatars?: boolean }) =>
      socket.emit('update_room_settings', { roomId, ...settings }),
    [roomId]
  );
  const kickUser = useCallback((userId: string) => socket.emit('kick_user', { roomId, userId }), [roomId]);
  const blockUser = useCallback((userId: string) => socket.emit('block_user', { roomId, userId }), [roomId]);
  const unblockUser = useCallback((userId: string) => socket.emit('unblock_user', { roomId, userId }), [roomId]);

  return {
    queue,
    isHost,
    isQueueOpen,
    maxMessagesPerUser,
    countdownSeconds,
    isProtected,
    isStageProtected,
    hideAvatars,
    joinStatus,
    joinError,
    roomJoinedReceived,
    // Backwards-compatible derived flags for existing consumers
    joined: joinStatus === 'ready',
    isReady: joinStatus === 'ready',
    isJoining: joinStatus === 'joining',
    errorMsg: joinError,
    showUpNextBanner,
    setShowUpNextBanner,
    roomEnded,
    nowPlayingId,
    activeMoment,
    activeMomentEnd,
    momentTemplates,
    queueMoments,
    showFullQueue,
    clients,
    blockedUsers,
    bigPictureViewers,
    stageLive,
    bpQRShown,
    bpLeaderboardShown,
    bpCountdownPaused,
    bpVideoPaused,
    displayQueue,
    doJoin,
    popTop,
    setNowPlaying,
    startMoment,
    endMoment,
    listMoments,
    addMoment,
    removeMoment,
    reorderMoments,
    updateShowFullQueue,
    goLive,
    stepAside,
    swapNowPlaying,
    toggleQueue,
    updateMaxMessages,
    updateCountdown,
    reorder,
    reorderOwnSongs,
    leave,
    endRoom,
    updatePassword,
    updateRoomSettings,
    kickUser,
    blockUser,
    unblockUser,
  };
}

function playBeepSound() {
  try {
    const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.3);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.35);
  } catch {
    // ignore
  }
}
